"use client";

import { useForm, Controller, useWatch } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import StepIndicator from "@/modules/account/advertising-agency/components/sign-up/StepIndicator";

import { useState, useMemo, useEffect } from "react";

import { AgreeNotification } from "@/modules/account/advertising-agency/components/pop-up/NotificationConsentModal";
import { useBackButtonGuard } from "@/hooks/useBackButtonGuard";
import CancelSignUpConfirmModal from "@/modules/account/advertising-agency/components/pop-up/CancelSignUpConfirmModal";
import { useRouter } from "next/navigation";
import TermsRevokeConfirmDialog from "@/modules/account/advertising-agency/components/pop-up/TermsRevokeConfirmDialog";
import { Typography } from "@/components/custom/typography";
import { CONSENT_TYPE } from "@/lib/constants";
import { TermOfService } from "@/modules/account/advertising-agency/components/pop-up/TermOfService";
import { TermOfServicePersonal } from "@/modules/account/advertising-agency/components/pop-up/TermOfServicePersonal";
import useFormPersist from "react-hook-form-persist";
import { useCommonCDByCodes } from "@/hooks/useCommonCD";
import { CommonCDType } from "@/types/common-cd";

type FormValues = {
  terms: boolean;
  privacy: boolean;
  personalRequired: boolean;
  companyRequired: boolean;
  marketingEmail: boolean;
  marketingSMS: boolean;
  consent_codes: string[];
};

export interface AdvSignUpTermProps {
  label: {
    title: string;
    subtitle: string;
    step1Description: string;
    termsTitle: string;
    termsDescription: string;
    termsViewButton: string;
    termsAgreeText: string;
    privacyTitle: string;
    privacyDescription: string;
    privacyViewButton: string;
    privacyAgreeText: string;
    notificationTitle: string;
    notificationDescription: string;
    notificationViewButton: string;
    notificationEmail: string;
    notificationSms: string;
    notificationAgreeText: string;
    buttonPrevious: string;
    buttonNext: string;
  };
}

export default function AdvSignUpTerm({ label }: AdvSignUpTermProps) {
  const { control, handleSubmit, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      terms: false,
      privacy: false,
      personalRequired: false,
      companyRequired: false,
      marketingEmail: false,
      marketingSMS: false,
      consent_codes: [],
    },
  });

  const [openRevokeDialog, setOpenRevokeDialog] = useState(false);
  const [revokeConfirmed, setRevokeConfirmed] = useState<() => void>(
    () => () => {},
  );

  const onSubmit = (data: FormValues) => {
    console.warn("동의 완료:", data);
  };

  const [openTerm, setOpenTerm] = useState(false);
  const [openTermPersonal, setOpenTermPersonal] = useState(false);
  // const [openContinue, setOpenContinue] = useState(false);
  const [openCancelSignUp, setOpenCancelSignUp] = useState(false);

  const [openNotification, setOpenNotification] = useState(false);
  const [isTermsEnabled, setIsTermsEnabled] = useState(false);
  const [isTermsPersonalEnabled, setIsTermsPersonalEnabled] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  // Hydration effect
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Form persistence - keep checkbox state
  useFormPersist("adv-terms-agreement", {
    watch,
    setValue,
    storage: isHydrated ? window.localStorage : undefined,
    exclude: [], // Don't exclude anything to keep all form state
  });

  const terms = useWatch({ control, name: "terms" });
  const privacy = useWatch({ control, name: "privacy" });
  const personalRequired = useWatch({ control, name: "personalRequired" });
  const companyRequired = useWatch({ control, name: "companyRequired" });
  const marketingEmail = useWatch({ control, name: "marketingEmail" });
  const marketingSMS = useWatch({ control, name: "marketingSMS" });
  const isAgreeEnabled = terms && privacy;
  const router = useRouter();

  // Intercept browser back button to show cancel confirmation modal
  useBackButtonGuard({
    onBackAttempt: () => setOpenCancelSignUp(true),
  });

  function groupConsent(consents: CommonCDType[]) {
    return {
      tos: consents.filter((c) => c.grp_sub_cd === "tos_argeement"),
      personalRequired: consents.filter((c) =>
        [
          CONSENT_TYPE.CONSENT_EMAIL,
          CONSENT_TYPE.CONSENT_NAME,
          CONSENT_TYPE.CONSENT_MOBILE,
          CONSENT_TYPE.CONSENT_TEL,
          CONSENT_TYPE.CONSENT_DEPARTMENT,
          CONSENT_TYPE.CONSENT_POSITION,
        ].includes(c.grp_sub_cd),
      ),
      companyRequired: consents.filter((c) =>
        [
          CONSENT_TYPE.CONSENT_COMPANY_NAME,
          CONSENT_TYPE.CONSENT_CERT_NUM,
          CONSENT_TYPE.CONSENT_COMPANY_ADDR,
          CONSENT_TYPE.CONSENT_COMPANY_TEL,
        ].includes(c.grp_sub_cd),
      ),
      marketing: consents.filter((c) =>
        [CONSENT_TYPE.MARKETING_EMAIL, CONSENT_TYPE.MARKETING_SMS].includes(
          c.grp_sub_cd,
        ),
      ),
    };
  }

  const { data: commonCdList } = useCommonCDByCodes(["consent_type"]);

  // Group consent data from API
  const groupedConsents = commonCdList?.data
    ? groupConsent(commonCdList.data)
    : null;

  // Generate consent codes based on current form values using useMemo to prevent infinite re-renders
  const currentConsentCodes = useMemo(() => {
    const codes: string[] = [];

    if (!groupedConsents) {
      return codes;
    }

    if (terms && groupedConsents?.tos) {
      codes.push(...groupedConsents.tos.map((item) => item.grp_sub_cd));
    }

    if (privacy && groupedConsents?.personalRequired) {
      codes.push(
        ...groupedConsents.personalRequired.map((item) => item.grp_sub_cd),
      );
    }

    if (personalRequired && groupedConsents?.personalRequired) {
      codes.push(
        ...groupedConsents.personalRequired.map((item) => item.grp_sub_cd),
      );
    }

    if (companyRequired && groupedConsents?.companyRequired) {
      codes.push(
        ...groupedConsents.companyRequired.map((item) => item.grp_sub_cd),
      );
    }

    if (marketingEmail && groupedConsents?.marketing) {
      const emailConsent = groupedConsents.marketing.find(
        (item) => item.grp_sub_cd === CONSENT_TYPE.MARKETING_EMAIL,
      );
      if (emailConsent) {
        codes.push(emailConsent.grp_sub_cd);
      }
    }

    if (marketingSMS && groupedConsents?.marketing) {
      const smsConsent = groupedConsents.marketing.find(
        (item) => item.grp_sub_cd === CONSENT_TYPE.MARKETING_SMS,
      );
      if (smsConsent) {
        codes.push(smsConsent.grp_sub_cd);
      }
    }

    return codes;
  }, [
    groupedConsents,
    terms,
    privacy,
    personalRequired,
    companyRequired,
    marketingEmail,
    marketingSMS,
  ]);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="w-full max-w-3xl mx-auto space-y-12 relative"
    >
      <div className="flex flex-col gap-3">
        <Typography variant="h2" tone="medium" className="font-bold">
          {label.title}
        </Typography>
        <Typography
          variant="body2"
          tone="description"
          className="whitespace-pre-line font-semibold"
        >
          {label.subtitle}
        </Typography>
      </div>

      {/* 서비스 이용 약관 */}
      <div className="flex flex-col gap-9">
        {/* 전체 동의 안내 */}
        <div className="flex flex-col gap-3 justify-between border-b border-gray-400 pb-6 ">
          <StepIndicator currentStep={1} />
          <Typography
            variant="body2"
            tone={"darknormal"}
            className="whitespace-pre-line"
          >
            {label.step1Description}
          </Typography>
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex justify-between items-start gap-3 space-y-1">
            <div className="flex-10 flex-col gap-3">
              <Typography variant={"subheading2"} tone="darkstrong">
                {label.termsTitle}
              </Typography>
              <Typography variant={"body2"} className="text-gray-725">
                {label.termsDescription}
              </Typography>
            </div>
            <button
              type="button"
              className="text-end flex-2 text-base font-semibold text-primary-400 underline underline-offset-2 cursor-pointer"
              onClick={() => setOpenTerm(true)}
            >
              {label.termsViewButton}
            </button>
          </div>
          <Controller
            name="terms"
            control={control}
            render={({ field }) => (
              <div className="flex items-center space-x-1 w-full bg-gray-100 rounded-xs p-1">
                <Checkbox
                  id="terms"
                  checked={field.value}
                  disabled={!isTermsEnabled}
                  onCheckedChange={(checked) => {
                    if (!checked) {
                      setOpenRevokeDialog(true);
                      setRevokeConfirmed(() => () => {
                        field.onChange(false);
                      });
                    } else {
                      field.onChange(true);
                    }
                  }}
                />

                <Typography variant="body3">{label.termsAgreeText}</Typography>
              </div>
            )}
          />
        </div>

        {/* 개인정보 수집 및 이용 동의 */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-start gap-3 space-y-1">
            <div className="flex-10 flex-col gap-3">
              <Typography variant={"subheading2"} tone="darkstrong">
                {label.privacyTitle}
              </Typography>

              <Typography variant={"body2"} className="text-gray-725">
                {label.privacyDescription}
              </Typography>
            </div>

            <button
              type="button"
              className="text-base font-semibold text-primary-400 underline underline-offset-2 cursor-pointer"
              onClick={() => setOpenTermPersonal(true)}
            >
              {label.privacyViewButton}
            </button>
          </div>
          <Controller
            name="privacy"
            control={control}
            render={({ field }) => (
              <div className="flex items-center space-x-1 w-full bg-gray-100 rounded-xs p-1">
                <Checkbox
                  id="privacy"
                  checked={field.value}
                  disabled={!isTermsPersonalEnabled}
                  onCheckedChange={(checked) => {
                    if (!checked) {
                      setOpenRevokeDialog(true);
                      setRevokeConfirmed(() => () => {
                        field.onChange(false);
                      });
                    } else {
                      field.onChange(true);
                    }
                  }}
                />
                <Typography variant="body3">
                  {label.privacyAgreeText}
                </Typography>
              </div>
            )}
          />
        </div>

        {/* 알림 수신 동의 (선택) */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-start gap-3 space-y-1">
            <div className="flex-10 flex-col gap-3">
              <Typography variant={"subheading2"} tone="darkstrong">
                {label.notificationTitle}
              </Typography>
              <Typography variant={"body2"} className="text-gray-725">
                {label.notificationDescription}
              </Typography>
            </div>
            <button
              type="button"
              className="text-base font-semibold text-primary-400 underline underline-offset-2 cursor-pointer"
              onClick={() => setOpenNotification(true)}
            >
              {label.notificationViewButton}
            </button>
            <AgreeNotification
              open={openNotification}
              onClose={() => setOpenNotification(false)}
              defaultCheckedEmail={marketingEmail}
              defaultCheckedSMS={marketingSMS}
              onAgree={({ agreeEmail, agreeSMS }) => {
                setValue("marketingEmail", agreeEmail);
                setValue("marketingSMS", agreeSMS);
              }}
            />
          </div>

          <div className="flex items-center space-x-3 w-full bg-gray-100 rounded-xs p-1">
            <div className="flex items-center space-x-1">
              <Controller
                name="marketingEmail"
                control={control}
                render={({ field }) => (
                  <>
                    <Checkbox
                      id="marketingEmail"
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        if (!checked) {
                          setOpenRevokeDialog(true);
                          setRevokeConfirmed(() => () => {
                            field.onChange(false);
                          });
                        } else {
                          field.onChange(true);
                        }
                      }}
                    />
                    <Typography
                      as="span"
                      variant="body3"
                      className="font-medium "
                    >
                      {label.notificationEmail}
                    </Typography>
                  </>
                )}
              />
            </div>

            <div className="flex items-center space-x-1">
              <Controller
                name="marketingSMS"
                control={control}
                render={({ field }) => (
                  <>
                    <Checkbox
                      id="marketingSMS"
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        if (!checked) {
                          setOpenRevokeDialog(true);
                          setRevokeConfirmed(() => () => {
                            field.onChange(false);
                          });
                        } else {
                          field.onChange(true);
                        }
                      }}
                    />
                    <Typography variant="body3">
                      {label.notificationSms} &nbsp;&nbsp;{" "}
                      {label.notificationAgreeText}
                    </Typography>
                  </>
                )}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Buttons */}
      <div className="flex gap-3.5 justify-end">
        <Button
          type="button"
          size="lg"
          tone="light"
          variant="secondary"
          className="border-2 border-gray-300 text-base"
          onClick={() => setOpenCancelSignUp(true)}
        >
          {label.buttonPrevious}
        </Button>

        <CancelSignUpConfirmModal
          open={openCancelSignUp}
          setOpen={setOpenCancelSignUp}
        />

        <Button
          type="button"
          size="lg"
          className="text-base"
          onClick={() => {
            if (isAgreeEnabled) {
              // Save consent codes to localStorage for the signup form
              if (typeof window !== "undefined") {
                window.localStorage.setItem(
                  "consent_codes",
                  JSON.stringify(currentConsentCodes),
                );
              }
              router.push("/advertising-agency/signup-form");
            }
          }}
          disabled={!isAgreeEnabled}
        >
          {label.buttonNext}
        </Button>
        <TermOfService
          open={openTerm}
          onClose={() => setOpenTerm(false)}
          onRequestCancel={() => {
            setOpenRevokeDialog(true);
            setRevokeConfirmed(() => () => {
              setValue("terms", false);
              setIsTermsEnabled(false);
            });
          }}
          defaultChecked={terms}
          onAgree={() => {
            setValue("terms", true);
            setIsTermsEnabled(true);
            setOpenTerm(false);
          }}
        />
        <TermOfServicePersonal
          open={openTermPersonal}
          onClose={() => setOpenTermPersonal(false)}
          onRequestCancel={() => {
            setOpenRevokeDialog(true);
            setRevokeConfirmed(() => () => {
              setValue("privacy", false);
              setIsTermsPersonalEnabled(false);
            });
          }}
          defaultChecked={privacy}
          onAgree={() => {
            setValue("privacy", true);
            setIsTermsPersonalEnabled(true);
            setOpenTermPersonal(false);
          }}
        />
        <TermsRevokeConfirmDialog
          open={openRevokeDialog}
          onClose={() => setOpenRevokeDialog(false)}
          onCancelSignUp={() => {
            revokeConfirmed();
            setOpenTerm(false);
            setOpenRevokeDialog(false);
            setOpenTermPersonal(false);
          }}
        />
        {/* <ContinueSignUpModal
          open={openContinue}
          onClose={() => setOpenContinue(false)}
          onContinue={() => {
            setOpenContinue(false);
          }}
        /> */}
      </div>

      {/* Bottom help text */}
    </form>
  );
}
